<template>
  <div class="p-6">
    <div v-if="section?.related_processes && section.related_processes.length > 0">
      <div class="mb-4">
        <h3 class="text-lg font-semibold mb-2 flex items-center gap-2">
          <i class="pi pi-sitemap" style="color: var(--primary-gold);"></i>
          <span style="color: var(--text-primary);">Related Process Domains</span>
        </h3>
        <p class="text-sm" style="color: var(--text-secondary);">
          These process domains operationalize the concepts described in this framework section.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="processId in section.related_processes"
          :key="processId"
          class="process-card p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md"
          style="background: var(--background-primary); border-color: var(--border-light);"
          @click="navigateToProcess(processId)"
        >
          <div class="flex items-start gap-3">
            <i class="pi pi-sitemap text-xl" style="color: var(--primary-gold);"></i>
            <div class="flex-1">
              <h4 class="font-semibold mb-1" style="color: var(--text-primary);">
                {{ processId }}
              </h4>
              <p class="text-sm mb-2" style="color: var(--text-secondary);">
                {{ getProcessTitle(processId) }}
              </p>
              <p class="text-xs" style="color: var(--text-muted);">
                {{ getProcessDescription(processId) }}
              </p>
            </div>
            <i class="pi pi-arrow-right" style="color: var(--text-muted);"></i>
          </div>
        </div>
      </div>

      <div v-if="section.related_processes.includes('all')" class="mt-6 p-4 rounded-lg" style="background: var(--background-secondary);">
        <div class="flex items-start gap-3">
          <i class="pi pi-info-circle text-xl" style="color: var(--primary-gold);"></i>
          <div>
            <p class="font-semibold mb-1" style="color: var(--text-primary);">All Process Domains</p>
            <p class="text-sm" style="color: var(--text-secondary);">
              This framework section relates to all 11 process domains.
              Navigate to the Process Domain to explore individual process areas.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-12">
      <i class="pi pi-sitemap text-4xl mb-4" style="color: var(--text-muted);"></i>
      <p style="color: var(--text-secondary);">No related process domains for this section.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Section } from '@/types/ashmatics';
import { useExplorerStore } from '@/stores/explorerStore';

const props = defineProps<{
  section: Section | null;
}>();

const explorerStore = useExplorerStore();

// Titles/descriptions come from the live process-domain data (loaded by the
// store's initialize()), so they stay in sync with the framework instead of a
// hand-maintained map.
const getProcessTitle = (processId: string): string => {
  return explorerStore.findSectionInDomain('process', processId)?.title || processId;
};

const getProcessDescription = (processId: string): string => {
  return (
    explorerStore.findSectionInDomain('process', processId)?.description ||
    'View process domain details'
  );
};

// Open the referenced process domain in the peek modal (no-op for the 'all'
// pseudo-id, which the info card below already explains).
const navigateToProcess = (processId: string) => {
  explorerStore.openRelatedArtifact('process', processId);
};
</script>

<style scoped>
.process-card:hover {
  border-color: var(--primary-gold);
}
</style>
